<script setup lang="ts">
import { ref, watch } from 'vue'

const props = withDefaults(defineProps<{
  sourceUrl: string
  resultUrl: string
  canEdit?: boolean
  canDownload?: boolean
  busy?: boolean
}>(), { canEdit: true, canDownload: true, busy: false })
const emit = defineEmits<{
  (event: 'edit', mode: 'overlay' | 'region'): void
  (event: 'download', type: 'image' | 'pdf'): void
}>()
const sourceFailed = ref(false)
const resultFailed = ref(false)
watch(() => props.sourceUrl, () => { sourceFailed.value = false })
watch(() => props.resultUrl, () => { resultFailed.value = false })

function download(type: 'image' | 'pdf') {
  if (!props.busy && props.canDownload && props.resultUrl && !resultFailed.value) emit('download', type)
}
</script>

<template>
  <section class="image-result-card">
    <header class="image-result-head">
      <h2>{{ $t('image2_result_title') }}</h2>
      <div class="image-result-actions">
        <button type="button" :disabled="busy || !canEdit || !sourceUrl || !resultUrl || sourceFailed || resultFailed" @click="emit('edit', 'overlay')">{{ $t('image2_btn_sample_overlay') }}</button>
        <button type="button" :disabled="busy || !canEdit || !sourceUrl || !resultUrl || sourceFailed || resultFailed" @click="emit('edit', 'region')">{{ $t('image2_btn_region_restore') }}</button>
        <el-dropdown trigger="click" :disabled="busy || !canDownload || !resultUrl || resultFailed" @command="download">
          <button type="button" class="image-download-button" :disabled="busy || !canDownload || !resultUrl || resultFailed" aria-haspopup="menu">
            {{ $t('image2_btn_download') }}<span class="image-download-caret" aria-hidden="true" />
          </button>
          <template #dropdown>
            <el-dropdown-menu class="enterprise-image-download-menu">
              <el-dropdown-item command="image">{{ $t('image2_download_image') }}</el-dropdown-item>
              <el-dropdown-item command="pdf">{{ $t('image2_download_pdf') }}</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </header>
    <p v-if="!canEdit" class="image-readonly" role="status">{{ $t('bizImage_readonly') }}</p>
    <div class="image-result-pair">
      <figure>
        <figcaption>{{ $t('image2_result_orig') }}</figcaption>
        <div class="image-result-frame">
          <img v-if="sourceUrl && !sourceFailed" :key="sourceUrl" :src="sourceUrl" crossorigin="anonymous" :alt="$t('image2_result_orig')" @error="sourceFailed = true" />
          <p v-else role="status">{{ $t('bizImage_error_image') }}</p>
        </div>
      </figure>
      <figure>
        <figcaption>{{ $t('image2_result_trans') }}</figcaption>
        <div class="image-result-frame">
          <img v-if="resultUrl && !resultFailed" :key="resultUrl" :src="resultUrl" crossorigin="anonymous" :alt="$t('image2_result_trans')" @error="resultFailed = true" />
          <p v-else role="status">{{ $t('bizImage_error_image') }}</p>
        </div>
      </figure>
    </div>
  </section>
</template>

<style scoped lang="scss">
.image-result-card { min-width: 0; padding: 22px; border: 1px solid #ebeef2; border-radius: 14px; background: #fff; box-shadow: 0 4px 14px #1f23290a; }
.image-result-head { display: flex; justify-content: space-between; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 16px; }
.image-result-head h2 { margin: 0; font-size: 17px; font-weight: 800; line-height: 25px; color: #1f2329; }
.image-result-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.image-result-actions button { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 34px; padding: 6px 12px; border: 1px solid #d9dee5; border-radius: 8px; background: #fff; color: #51565d; font: inherit; font-size: 12px; line-height: 20px; cursor: pointer; white-space: nowrap; }
.image-result-actions button:hover:not(:disabled) { border-color: #2b7de9; color: #2b7de9; }
.image-result-actions button.image-download-button { background: #2b7de9; color: #fff; border-color: #2b7de9; font-weight: 600; }
.image-result-actions button:disabled { cursor: not-allowed; background: #f7f8fa; border-color: #e9edf3; color: #a6adb7; }
.image-result-actions button.image-download-button:disabled { background: #dbeafe; border-color: #dbeafe; color: #fff; }
.image-download-caret { width: 13px; height: 13px; background: currentColor; mask: url('/assets/images/workbench/image-chevron-down.svg') center / contain no-repeat; }
.image-result-pair { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
figure { min-width: 0; margin: 0; }
figcaption { margin-bottom: 8px; color: #51565d; font-size: 12.5px; line-height: 20px; }
.image-result-frame { height: 340px; display: flex; align-items: center; justify-content: center; overflow: hidden; border-radius: 8px; border: 1px solid #eef1f5; background: #fafafb; }
.image-result-frame img { display: block; width: 100%; height: 100%; object-fit: contain; }
.image-result-frame p, .image-readonly { color: #8f959e; font-size: 12px; line-height: 20px; padding: 12px; text-align: center; }
.image-readonly { padding: 0; text-align: start; margin: 0 0 12px; }
@media (max-width: 650px) { .image-result-card { padding: 18px; } .image-result-pair { grid-template-columns: minmax(0, 1fr); } .image-result-frame { height: 300px; } }
</style>
